import { db } from '../database/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

const EXPENSES_COLLECTION = 'expenses';
const ADVANCES_COLLECTION = 'advances';
const EXPENSE_CATEGORIES_COLLECTION = 'expenseCategories';

// ============================================
// EXPENSE MANAGEMENT
// ============================================

// Get all expenses
export const getAllExpenses = async () => {
  try {
    const expensesRef = collection(db, EXPENSES_COLLECTION);
    const q = query(expensesRef, orderBy('expenseDate', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw error;
  }
};

// Get expense by ID
export const getExpenseById = async (expenseId) => {
  try {
    const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
    const expenseDoc = await getDoc(expenseRef);
    
    if (expenseDoc.exists()) {
      return { id: expenseDoc.id, ...expenseDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching expense:', error);
    throw error;
  }
};

// Add new expense
export const addExpense = async (expenseData) => {
  try {
    const expensesRef = collection(db, EXPENSES_COLLECTION);
    const newExpense = {
      ...expenseData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(expensesRef, newExpense);
    return { id: docRef.id, ...newExpense };
  } catch (error) {
    console.error('Error adding expense:', error);
    throw error;
  }
};

// Update expense
export const updateExpense = async (expenseId, expenseData) => {
  try {
    const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
    const updateData = {
      ...expenseData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(expenseRef, updateData);
    return { id: expenseId, ...updateData };
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

// Delete expense
export const deleteExpense = async (expenseId) => {
  try {
    const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
    await deleteDoc(expenseRef);
    return true;
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

// ============================================
// ADVANCE MANAGEMENT
// ============================================

// Get all advances
export const getAllAdvances = async () => {
  try {
    const advancesRef = collection(db, ADVANCES_COLLECTION);
    const q = query(advancesRef, orderBy('requestDate', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching advances:', error);
    throw error;
  }
};

// Get advances by staff ID
export const getAdvancesByStaffId = async (staffId) => {
  try {
    const advancesRef = collection(db, ADVANCES_COLLECTION);
    const q = query(
      advancesRef,
      where('staffId', '==', staffId),
      orderBy('requestDate', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching staff advances:', error);
    throw error;
  }
};

// Add new advance
export const addAdvance = async (advanceData) => {
  try {
    const advancesRef = collection(db, ADVANCES_COLLECTION);
    const newAdvance = {
      ...advanceData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(advancesRef, newAdvance);
    return { id: docRef.id, ...newAdvance };
  } catch (error) {
    console.error('Error adding advance:', error);
    throw error;
  }
};

// Update advance
export const updateAdvance = async (advanceId, advanceData) => {
  try {
    const advanceRef = doc(db, ADVANCES_COLLECTION, advanceId);
    const updateData = {
      ...advanceData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(advanceRef, updateData);
    return { id: advanceId, ...updateData };
  } catch (error) {
    console.error('Error updating advance:', error);
    throw error;
  }
};

// Delete advance
export const deleteAdvance = async (advanceId) => {
  try {
    const advanceRef = doc(db, ADVANCES_COLLECTION, advanceId);
    await deleteDoc(advanceRef);
    return true;
  } catch (error) {
    console.error('Error deleting advance:', error);
    throw error;
  }
};

// Approve/Reject advance
export const updateAdvanceStatus = async (advanceId, status, approvedBy, remarks = '') => {
  try {
    const advanceRef = doc(db, ADVANCES_COLLECTION, advanceId);
    const updateData = {
      status,
      approvedBy,
      approvalDate: serverTimestamp(),
      remarks,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(advanceRef, updateData);
    return { id: advanceId, ...updateData };
  } catch (error) {
    console.error('Error updating advance status:', error);
    throw error;
  }
};

// ============================================
// EXPENSE CATEGORIES
// ============================================

// Get all expense categories
export const getAllExpenseCategories = async () => {
  try {
    const categoriesRef = collection(db, EXPENSE_CATEGORIES_COLLECTION);
    const q = query(categoriesRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching expense categories:', error);
    throw error;
  }
};

// Add expense category
export const addExpenseCategory = async (categoryData) => {
  try {
    const categoriesRef = collection(db, EXPENSE_CATEGORIES_COLLECTION);
    const newCategory = {
      ...categoryData,
      createdAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(categoriesRef, newCategory);
    return { id: docRef.id, ...newCategory };
  } catch (error) {
    console.error('Error adding expense category:', error);
    throw error;
  }
};

// ============================================
// ANALYTICS & REPORTS
// ============================================

// Get expense summary by date range
export const getExpenseSummary = async (startDate, endDate) => {
  try {
    const expensesRef = collection(db, EXPENSES_COLLECTION);
    const q = query(
      expensesRef,
      where('expenseDate', '>=', Timestamp.fromDate(new Date(startDate))),
      where('expenseDate', '<=', Timestamp.fromDate(new Date(endDate)))
    );
    const snapshot = await getDocs(q);
    
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    
    return {
      totalExpenses: expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0),
      count: expenses.length,
      byCategory: groupByCategory(expenses),
      byStatus: groupByStatus(expenses),
    };
  } catch (error) {
    console.error('Error fetching expense summary:', error);
    throw error;
  }
};

// Helper functions
const groupByCategory = (expenses) => {
  const grouped = {};
  expenses.forEach(exp => {
    const category = exp.category || 'Uncategorized';
    if (!grouped[category]) {
      grouped[category] = { count: 0, total: 0 };
    }
    grouped[category].count++;
    grouped[category].total += parseFloat(exp.amount) || 0;
  });
  return grouped;
};

const groupByStatus = (expenses) => {
  const grouped = {};
  expenses.forEach(exp => {
    const status = exp.status || 'pending';
    if (!grouped[status]) {
      grouped[status] = { count: 0, total: 0 };
    }
    grouped[status].count++;
    grouped[status].total += parseFloat(exp.amount) || 0;
  });
  return grouped;
};
